import { IMediaPermissions } from '@gem/call/CallStore/Call.types';
import { ICallCreatedInfo, ICallInfo, IWaitingRoomMember } from '@gem/call/Model/callModel.types';
import { initCallsApi } from '@gem/call/WsApi/wsCallsApi/WsCallsApi';
import { IDevices } from '@gem/call/tasks/getDefaultDevices';
import { t } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { ServerErrorCode } from 'APP/model/error/error.constants';
import { callModel, mainCallModel } from 'APP/model/model';
import { ProjectUrls } from 'APP/packages/projectUrls/ProjectUrls';
import Entities from 'STORE';
import { MainCall } from 'STORE/Calls/MainCall/MainCall';

const createNewCall = async (call: MainCall): Promise<ICallCreatedInfo | null> => {
  try {
    return await mainCallModel.createCall({
      groupId: call.groupId,
      providerType: call.providerType,
    });
  } catch (e) {
    Entities.Calls.delete(call.groupId, call.channelId);
    if (e?.message === ServerErrorCode.HasNoPermissionsToPerformAction) {
      Tasks.app.showAlert(t('no_permission_alert'));
      return null;
    }

    if (e?.message === ServerErrorCode.HasNoPermissionsToCall) {
      Tasks.app.showAlert(t('privacy_settings_call_alert'));
      return null;
    }
    Tasks.app.showAlert(t('calls_connection_error'));
    return null;
  }
};

const createExistingCall = async (call: MainCall): Promise<ICallInfo | null> => {
  try {
    const channelId = call.channelId!;

    return await callModel.getCallInfo({
      channelId,
      providerType: call.providerType,
    });
  } catch (e) {
    console.error(e);
    Entities.Calls.delete(call.groupId, call.channelId);
    Tasks.app.showAlert(t('calls_connection_error'));
    return null;
  }
};

interface ICreateCallProps {
  groupId?: string;
  channelId: string | null;
  withAudio: boolean;
  withVideo: boolean;
  devicesIds?: IDevices;
  waitingRoomMembers?: IWaitingRoomMember[];
  isWaitingRoomEnabled?: boolean;
  isGuestsAllowed?: boolean;
  mediaPermissions?: IMediaPermissions;
}

export const startCall = async ({
  groupId,
  channelId = null,
  withAudio,
  withVideo,
  devicesIds,
  waitingRoomMembers,
  isWaitingRoomEnabled,
  isGuestsAllowed,
  mediaPermissions,
}: ICreateCallProps): Promise<void> => {
  // TodO: [CALLS-B2B] - нужно централизованно удалять и инитить классы сокета звонка
  initCallsApi(ProjectUrls.wsCallsUrl);
  Entities.IncomingCalls.delete(channelId, groupId);
  const group = groupId ? Entities.GroupsStore.getGroupById(groupId) : null;
  let mainCall = null;

  if (groupId) {
    mainCall = Entities.Calls.getCallByGroupId(groupId);
  }

  if (!mainCall && channelId) {
    mainCall = Entities.Calls.getCallByChannelId(channelId);
  }

  if (!mainCall) {
    mainCall = new MainCall({
      myId: Entities.usersStore.Me.id,
      calls: Entities.Calls,
      groupId,
      channelId,
      initialDevicesIds: {
        audioinput: devicesIds?.audioinput || null,
        audiooutput: devicesIds?.audiooutput || null,
        videoinput: devicesIds?.videoinput || null,
      },
      mediaPermissions,
      providerType: Entities.Calls.callsProvider,
      withoutTimer: channelId ? true : !group?.isP2P,
      initialMedia: {
        withAudio,
        withVideo,
      },
      waitingRoomMembers,
      isWaitingRoomEnabled,
      isGuestsAllowed,
    });

    Entities.Calls.add(mainCall);
  }

  let callInfo;

  if (channelId) {
    callInfo = await createExistingCall(mainCall);
  } else {
    const response = await createNewCall(mainCall);

    if (!response) {
      return;
    }
    mainCall.setChannelId(response.channelId);
    callInfo = await createExistingCall(mainCall);
  }

  if (!callInfo) {
    return;
  }

  mainCall.setReadyToStart({
    callInfo,
    isInitiator: callInfo.initiatorId === Entities.usersStore.Me.id,
  });
};
